import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DoctorNavbar from '../components/DoctorNavbar';
import { toast } from 'react-toastify';

function money(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return '$0.00';
  return `$${v.toFixed(2)}`;
}

function statusBadge(status) {
  const map = {
    PAYABLE: { bg: '#DBEAFE', color: '#1D4ED8', label: 'Payable' },
    NO_SHOW_PATIENT_PAYABLE: { bg: '#FEF3C7', color: '#B45309', label: 'Patient no-show' },
    PAID: { bg: '#D1FAE5', color: '#047857', label: 'Paid' },
    DOCTOR_NO_SHOW_UNPAID: { bg: '#FEE2E2', color: '#B91C1C', label: 'Unpaid (no join)' },
    NOT_ELIGIBLE: { bg: '#F3F4F6', color: '#4B5563', label: 'Not eligible' },
    REFUNDED: { bg: '#E0E7FF', color: '#4338CA', label: 'Refunded' },
    PENDING_REVIEW: { bg: '#F3F4F6', color: '#6B7280', label: 'Pending' },
    HOLD: { bg: '#FFEDD5', color: '#C2410C', label: 'Hold' },
  };
  const s = map[status] || { bg: '#F3F4F6', color: '#4B5563', label: status };
  return (
    <span className="badge rounded-pill px-2 py-1" style={{ background: s.bg, color: s.color, fontSize: 11 }}>
      {s.label}
    </span>
  );
}

function DoctorEarningsHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [tab, setTab] = useState('earnings');

  useEffect(() => {
    const load = async () => {
      try {
        const session = await api.get('doctor/session-check/');
        if (!session.data.authenticated) {
          navigate('/doctor/login');
          return;
        }
        const [sumRes, earnRes, payRes] = await Promise.all([
          api.get('payments/doctor/earnings/summary/'),
          api.get('payments/doctor/earnings/history/'),
          api.get('payments/doctor/payouts/history/'),
        ]);
        setSummary(sumRes.data);
        setEarnings(Array.isArray(earnRes.data.results) ? earnRes.data.results : []);
        setPayouts(Array.isArray(payRes.data.results) ? payRes.data.results : []);
      } catch (err) {
        if (err.response?.status === 401) navigate('/doctor/login');
        else toast.error(err.response?.data?.error || 'Failed to load earnings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F3F6FB' }}>
        <DoctorNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  const cards = [
    { label: 'Total Gross', value: money(summary?.total_gross), sub: 'Consultation fees', icon: 'bi-cash-stack', bg: '#DBEAFE', color: '#2563EB' },
    { label: 'Platform Fee', value: money(summary?.total_platform_fee), sub: `$${Number(summary?.platform_fee_per_booking || 5).toFixed(0)} / booking`, icon: 'bi-building', bg: '#EDE9FE', color: '#7C3AED' },
    { label: 'Your Earnings', value: money(summary?.total_earned), sub: 'After platform fee', icon: 'bi-wallet2', bg: '#D1FAE5', color: '#059669' },
    { label: 'Paid by Admin', value: money(summary?.total_paid), sub: 'Received so far', icon: 'bi-check-circle', bg: '#FEF3C7', color: '#D97706' },
    { label: 'Remaining', value: money(summary?.remaining), sub: 'Yet to be paid', icon: 'bi-hourglass-split', bg: '#FEE2E2', color: '#DC2626' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#F3F6FB' }}>
      <DoctorNavbar />
      <div className="container-fluid px-3 px-md-4 py-4" style={{ maxWidth: 1100 }}>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
          <div className="d-flex align-items-start gap-2">
            <button
              type="button"
              className="btn btn-link text-dark text-decoration-none p-0 mt-1"
              onClick={() => navigate('/doctor/settings')}
            >
              <i className="bi bi-arrow-left fs-5" />
            </button>
            <div>
              <h2 className="fw-bold mb-0">Payment History</h2>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                Earnings, platform fee ($5/booking), and admin payouts
              </p>
            </div>
          </div>
          {!summary?.has_bank_details && (
            <button
              type="button"
              className="btn btn-outline-primary rounded-3"
              onClick={() => navigate('/doctor/bank-details')}
            >
              Add bank details
            </button>
          )}
        </div>

        <div className="row g-3 mb-4">
          {cards.map((c) => (
            <div key={c.label} className="col-6 col-lg">
              <div className="bg-white rounded-4 border h-100 p-3" style={{ borderColor: '#E8EDF5' }}>
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 mb-2"
                  style={{ width: 34, height: 34, background: c.bg, color: c.color }}
                >
                  <i className={`bi ${c.icon}`} />
                </div>
                <p className="text-muted mb-1" style={{ fontSize: 12 }}>{c.label}</p>
                <h5 className="fw-bold mb-0">{c.value}</h5>
                <small className="text-muted">{c.sub}</small>
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex gap-3 mb-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
          {[
            { key: 'earnings', label: 'Booking earnings' },
            { key: 'payouts', label: 'Admin payouts' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className="btn btn-link text-decoration-none px-1 py-2 rounded-0"
              style={{
                color: tab === t.key ? '#2563EB' : '#6B7280',
                fontWeight: tab === t.key ? 700 : 500,
                borderBottom: tab === t.key ? '2px solid #2563EB' : '2px solid transparent',
                marginBottom: -1,
                fontSize: 14,
              }}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-4 border overflow-hidden" style={{ borderColor: '#E8EDF5' }}>
          {tab === 'earnings' && (
            earnings.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2" />
                No earnings yet. After completed consultations, amounts appear here.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table mb-0 align-middle">
                  <thead style={{ background: '#F8FAFC' }}>
                    <tr>
                      {['Date', 'Patient', 'Gross', 'Platform $5', 'Your amount', 'Status'].map((h) => (
                        <th key={h} className="border-0 text-muted text-uppercase px-3 py-3" style={{ fontSize: 11 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.map((e) => (
                      <tr key={e.id}>
                        <td className="px-3 py-3" style={{ fontSize: 13 }}>
                          {e.date || '—'}
                          <div className="text-muted" style={{ fontSize: 11 }}>Appt #{e.appointment_id}</div>
                        </td>
                        <td className="px-3 py-3" style={{ fontSize: 13 }}>{e.patient_name}</td>
                        <td className="px-3 py-3 fw-semibold">{money(e.consultation_fee)}</td>
                        <td className="px-3 py-3 text-muted">-{money(e.platform_fee)}</td>
                        <td className="px-3 py-3 fw-bold text-success">{money(e.doctor_amount)}</td>
                        <td className="px-3 py-3">{statusBadge(e.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === 'payouts' && (
            payouts.length === 0 ? (
              <div className="text-center py-5 text-muted px-3">
                <i className="bi bi-clock-history fs-1 d-block mb-2" />
                No admin payouts yet. When admin pays you, history will show here.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table mb-0 align-middle">
                  <thead style={{ background: '#F8FAFC' }}>
                    <tr>
                      {['Paid on', 'Bookings', 'Platform fees', 'Amount paid', 'Status', 'Note'].map((h) => (
                        <th key={h} className="border-0 text-muted text-uppercase px-3 py-3" style={{ fontSize: 11 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((p) => (
                      <tr key={p.id}>
                        <td className="px-3 py-3" style={{ fontSize: 13 }}>
                          {p.paid_at
                            ? new Date(p.paid_at).toLocaleString('en-GB', {
                                day: 'numeric', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })
                            : '—'}
                        </td>
                        <td className="px-3 py-3">{p.earnings_count}</td>
                        <td className="px-3 py-3 text-muted">{money(p.platform_fee_total)}</td>
                        <td className="px-3 py-3 fw-bold text-success">{money(p.amount)}</td>
                        <td className="px-3 py-3">
                          <span className="badge rounded-pill bg-success">{p.status}</span>
                        </td>
                        <td className="px-3 py-3 text-muted small">{p.reference_note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default DoctorEarningsHistory;
